/**
 * The Email view's sidebar (activity bar → Email; Outlook-style mail + calendar): Compose, the inbox with its
 * tabs (Priority, then Gmail-style All, Clients, Team, Promotions, Updates; "N new" each), the other folders, across every
 * mailbox I'm in unless the Mailbox filter picks one, the labels with one row per name ("+" adds one); Calendar and Contacts have their own views (SidebarCalendar, SidebarContacts). State and unread counts come from the workspace's MailboxViewModel
 * (useMail), the same one the Mail screen shows; the selection follows the active tab's route.
 */
import type { MailTabId } from "@bundle/contracts";
import { Shell, type MailView, type OpenTarget } from "@bundle/core";
import * as Mail from "@bundle/core/logic/mail";
import { IconButton, MAIL_FOLDER_ICONS, MAIL_VIEWS, SidebarNavRow, Text, TextField, mailTabIcon, openComposer, useFeatureAccess, useLocale, useT, useViewModel, useWorkspace, type MailServices } from "@bundle/ui";
import { useState } from "react";

type Props = { mail: MailServices; activeUrl: string; open: (url: string, target?: OpenTarget) => void };

export function SidebarMail({ mail, activeUrl, open }: Props) {
  const t = useT();
  const access = useFeatureAccess();
  const ws = useWorkspace();
  const s = useViewModel(mail.mailbox);
  const locale = useLocale();
  const active = Shell.parseUrl(activeUrl);
  const inMail = active?.type === "mail";
  const label = inMail ? active.label : undefined;
  // The Mailbox filter stays in the route from folder to folder.
  const scope = inMail ? { ...(active.mailboxId === undefined ? {} : { mailboxId: active.mailboxId }), ...(active.group === undefined ? {} : { group: active.group }) } : {};
  const boxId = s.mailboxId;
  const folder: MailView | undefined = inMail && label === undefined ? (active.folder ?? "inbox") : undefined;
  const [naming, setNaming] = useState<string | undefined>(undefined);
  const addLabel = async () => {
    const name = (naming ?? "").trim();
    setNaming(undefined);
    if (name !== "") await mail.mailbox.createLabel({ name, color: "gray" });
  };
  // No tab in the route: the one Mail picked (Priority unless it's empty).
  const tab: MailTabId = (inMail ? active.tab : undefined) ?? s.tab;
  const inboxUnread = Mail.mailInboxUnread(s.mailboxes, scope.mailboxId);

  const go = (r: { folder?: MailView; tab?: MailTabId; label?: string }) => {
    const route: Shell.Route = { type: "mail", ws: ws.slug, ...scope };
    if (r.folder !== undefined && r.folder !== "inbox") route.folder = r.folder;
    if (r.tab !== undefined) route.tab = r.tab;
    if (r.label !== undefined) route.label = r.label;
    open(Shell.formatUrl(route));
  };
  const compose = () => {
    if (boxId !== undefined) openComposer({ mailboxId: boxId, mode: "new", to: [], cc: [], subject: "", text: "" });
  };

  return (
    <>
      <div className="sidebar-view-header">
        <Text variant="headline" weight="semibold">
          {t("shell.view.email")}
        </Text>
        {/* No Compose while Email is look-only (Settings → Features). */}
        {access("mail") === "full" ? <IconButton icon="edit" size="sm" accessibilityLabel={t("mail.compose")} onPress={compose} disabled={boxId === undefined} testID="sidebar-mail-compose" /> : null}
      </div>
      {s.status === "empty" ? (
        <div className="sidebar-view-note">
          <Text variant="footnote" color="text.secondary">
            {t("mail.noMailbox.title")}
          </Text>
        </div>
      ) : (
        <div className="sidebar-group" data-testid="sidebar-mail-folders">
          {MAIL_VIEWS.map((f) => (
            <div key={f} className="sidebar-group">
              <SidebarNavRow
                icon={MAIL_FOLDER_ICONS[f]}
                label={t(`mail.folder.${f}`)}
                badge={f === "inbox" && inboxUnread > 0 ? inboxUnread : undefined}
                selected={folder === f && (f !== "inbox" || s.tabDefs.length === 0)}
                onPress={() => go({ folder: f })}
                testID={`sidebar-mail-folder-${f}`}
              />
              {f === "inbox" ? (
                <div className="sidebar-subrow sidebar-group">
                  {Mail.stripTabs(s.tabDefs).map((def) => {
                    const newCount = (def.kind === "priority" ? s.priority?.newCount : s.tabs.find((x) => x.tab === def.id)?.newCount) ?? 0;
                    return (
                      <SidebarNavRow
                        key={def.id}
                        icon={mailTabIcon(def)}
                        label={Mail.mailTabLabel(def, locale)}
                        badge={newCount > 0 ? newCount : undefined}
                        selected={folder === "inbox" && tab === def.id}
                        onPress={() => {
                          // Mail already open: its list switches now, before the route does.
                          if (inMail) mail.mailbox.setTab(def.id);
                          go({ tab: def.id });
                        }}
                        testID={`sidebar-mail-tab-${def.id}`}
                      />
                    );
                  })}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
      {s.status === "empty" ? null : (
        <div className="sidebar-group" data-testid="sidebar-mail-labels">
          <div className="sidebar-view-label">
            <Text variant="caption" weight="semibold" color="text.tertiary">
              {t("mail.labels")}
            </Text>
            {access("mail") === "full" ? <IconButton icon="plus" size="sm" accessibilityLabel={t("mail.label.new")} onPress={() => setNaming("")} testID="sidebar-mail-label-new" /> : null}
          </div>
          {naming === undefined ? null : (
            <TextField value={naming} onChange={setNaming} onSubmit={() => void addLabel()} onCancel={() => setNaming(undefined)} placeholder={t("mail.label.namePlaceholder")} accessibilityLabel={t("mail.label.new")} autoFocus testID="sidebar-mail-label-name" />
          )}
          {Mail.mergedMailLabels(s.labels).map((l) => (
            <SidebarNavRow key={l.id} icon="tag" label={l.name} selected={label !== undefined && l.ids.includes(label)} onPress={() => go({ label: l.id })} testID={`sidebar-mail-label-${l.id}`} />
          ))}
        </div>
      )}
    </>
  );
}

